import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../helpers/api";
import { ShowToast } from "../../../components/toast";
import type {
  PricingConfigResponse,
  UpdateConfigPayload,
} from "../types";

export const usePricingConfig = (userId: string | null) => {
  return useQuery<PricingConfigResponse>({
    queryKey: ["pricing-config", userId],
    queryFn: () => api.pricing.getConfig(userId!),
    enabled: !!userId,
  });
};

export const useUpdatePricingConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: UpdateConfigPayload;
    }) => api.pricing.updateConfig(userId, data),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Pricing config updated" });
      queryClient.invalidateQueries({ queryKey: ["pricing-config"] });
    },
    onError: (error: any) => {
      ShowToast({
        type: "error",
        message:
          error.response?.data?.message || "Failed to update pricing config",
      });
    },
  });
};

export const useResetPricingConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      scope,
    }: {
      userId: string;
      scope: string;
    }) => api.pricing.resetConfig(userId, { scope }),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Pricing config reset to defaults" });
      queryClient.invalidateQueries({ queryKey: ["pricing-config"] });
    },
    onError: (error: any) => {
      ShowToast({
        type: "error",
        message:
          error.response?.data?.message || "Failed to reset pricing config",
      });
    },
  });
};

export const useDeletePricingRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      ruleId,
    }: {
      userId: string;
      ruleId: string;
    }) => api.pricing.deleteRule(userId, ruleId),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Rule deleted" });
      queryClient.invalidateQueries({ queryKey: ["pricing-config"] });
    },
    onError: (error: any) => {
      ShowToast({
        type: "error",
        message: error.response?.data?.message || "Failed to delete rule",
      });
    },
  });
};

